import { Grid, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import { Alert } from '@mui/lab';
import * as api from "api/Knowledge";
import { DefaultListParameters } from 'assets/appConfig';
import BoxDrawer from 'components/BoxDrawer';
import { RequestIndicator, SpinnerIndicator } from 'components/CallServer';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiMessageBox from 'components/MuiExtras/MessageBox/MuiMessageBox';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from 'components/TextFieldCounter';
import UserFullProfileLink from 'components/UserFullProfileLink';
import UserProfileAvatar from 'components/UserProfileAvatar';
import { Formaters } from 'lib/Formarters';
import { PublicationSocialActionResult, PublicationThanksReviewDto } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    data: PublicationThanksReviewDto;
    lastError: any;
    loading: boolean;
    updatingThanks: boolean;
    offset: number;
    message: string;
}

interface IProps {
    publicationId: string;
    startDate?: Date;
    endDate?: Date;
    canAddThanks: boolean;
    onUpdate?: (data: PublicationSocialActionResult) => void;
    onClose: () => void;
}

type Props = IProps & WithTranslation & WithSnackbar;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class PublicationThanksReviewDrawer extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            lastError: null,
            loading: true,
            updatingThanks: false,
            offset: 0,
            message: null,
        };
    }

    ///
    private loadData(offset: number) {
        this.setState({ loading: true, updatingThanks: false, data: null });

        api.getThanksReviewList(
            this.props.publicationId,
            this.props.startDate,
            this.props.endDate,
            offset,
            DefaultListParameters.drawerRecordsByPage
        )
            .then((data) => {
                this.setState({
                    data,
                    loading: false,
                    offset: this.state.offset,
                    message: data.userThanked ? data.userThanked.message : null
                });
            })
            .catch((error) => {
                this.setState({ loading: false, offset: 0, lastError: error });
            });
    }

    ///
    private addThanks(message: string) {

        this.setState({ updatingThanks: true });

        api.addThanks(this.props.publicationId, message)
            .then((result) => {
                this.props.snackbar.showMessage(this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.AddThanksSuccess"), "success");
                if (result && this.props.onUpdate) this.props.onUpdate(result);
                this.props.onClose();
            })
            .catch((error) => {
                this.setState({ updatingThanks: false, lastError: error });
            });
    }

    ///
    private removeThanks() {

        this.setState({ updatingThanks: true });

        api.removeThanks(this.props.publicationId)
            .then((result) => {
                this.props.snackbar.showMessage(this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.DeleteThanksSuccess"), "success");
                if (result && this.props.onUpdate) this.props.onUpdate(result);
                this.props.onClose();
            })
            .catch((error) => {
                this.setState({ updatingThanks: false, lastError: error });
            });
    }

    ///
    private renderNewThanks() {
        const { data: { userThanked }, message } = this.state;
        return (
            <MuiContainer>
                <MuiPaper dense elevation={0}>

                    <Typography variant="caption">
                        <b>
                            {
                                userThanked
                                    ? this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.UserThankedText")
                                    : this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.UserNoThankText")
                            }
                        </b>
                    </Typography>

                    <TextFieldCounter
                        autoFocus={userThanked == null}
                        fullWidth
                        multiline
                        maxRows="3"
                        margin="dense"
                        placeholder={
                            message == null
                                ? this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.MessagePlaceholder")
                                : null
                        }
                        maxLength={1500}
                        value={message}
                        onChange={
                            (message) => {
                                this.setState({ message });
                            }
                        }
                    />

                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item>
                            <MuiRegularButton
                                color="success"
                                disabled={message == null || message.length == 0}
                                onClick={() => {
                                    this.addThanks(message);
                                }}
                            >
                                {userThanked ? this.props.t("Common.Buttons.UpdateText") : this.props.t("Common.Buttons.ThanksText")}
                            </MuiRegularButton>

                            {
                                userThanked &&
                                <MuiRegularButton
                                    color="error"
                                    onClick={
                                        () => {
                                            this.removeThanks();
                                        }
                                    }
                                >
                                    <span>{this.props.t("Common.Buttons.DeleteText")}</span>
                                </MuiRegularButton>
                            }
                        </Grid>
                    </Grid>

                </MuiPaper>
            </MuiContainer>
        );
    }

    ///
    private renderBox() {
        const { data } = this.state;

        return (
            <div>
                {this.props.canAddThanks && this.renderNewThanks()}
                {
                    data.total == 0
                        ? <Alert severity="info">{this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.ThereAreNoThanks")}</Alert>
                        : (
                            <div>
                                {
                                    this.props.canAddThanks &&
                                    <Typography variant="h6"><b>{this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.WhoThanksText")}:</b></Typography>
                                }

                                <List dense disablePadding>
                                    {
                                        data.items.map((v, index) => {
                                            return (
                                                <ListItem key={index} disableGutters>
                                                    <ListItemAvatar>
                                                        <UserProfileAvatar user={v.user} />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2" component="div">
                                                                <b><UserFullProfileLink user={v.user} />, {Formaters.relativeTime(v.date)}</b>
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" component="div">
                                                                {v.message}
                                                            </Typography>
                                                        }
                                                        primaryTypographyProps={{ noWrap: true }}
                                                    />
                                                </ListItem>
                                            );
                                        })
                                    }
                                </List>
                            </div>
                        )
                }
            </div>
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {
        const { onClose } = this.props;
        const { data, lastError, loading, updatingThanks, offset } = this.state;

        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={onClose}
                title={this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.BoxTitle")}
            >
                <MuiPaper dense fullHeight elevation={0}>

                    <RequestIndicator type="post" open={updatingThanks} />

                    <GridEx container direction="column" wrap="nowrap" fullHeight>

                        <GridEx item xs overflow="auto">
                            {
                                lastError
                                    ? <MuiMessageBox variant="error" message={this.props.t("Views.Timeline.Dialogs.PublicationThanksReviewDrawer.LoadError")} />
                                    : loading
                                        ? <SpinnerIndicator />
                                        : this.renderBox()
                            }
                        </GridEx>

                        {
                            !lastError && !loading && data.items && data.total > 0 &&
                            <GridEx item>
                                <MuiPagination
                                    limit={DefaultListParameters.drawerRecordsByPage}
                                    offset={offset}
                                    total={data.total}
                                    onClick={(e, offset) => this.loadData(offset)}
                                />
                            </GridEx>
                        }
                    </GridEx>

                </MuiPaper>

            </BoxDrawer>
        );
    }
}

export default withSnackbar(withTranslation()(PublicationThanksReviewDrawer));